/*
 * TanStack Query client. Defaults: 30 s fresh, no retry on 4xx, two retries otherwise. A failed mutation
 * shows an error toast unless it sets `meta: { silent: true }` (forms that show the error inline).
 * Optional `meta.success` shows a success toast with that text.
 */
import { MutationCache, QueryClient } from '@tanstack/react-query'
import { isApiError } from '@/api/client'
import { toast } from '@/components/ui/Toast'

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { silent?: boolean; success?: string }
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (count, error) => {
        if (isApiError(error) && error.status >= 400 && error.status < 500) return false
        return count < 2
      },
    },
    mutations: { retry: false },
  },
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.silent) return
      if (isApiError(error) && error.status === 401) return // the session-expired dialog handles it
      toast.error(error)
    },
    onSuccess: (_data, _vars, _ctx, mutation) => {
      if (mutation.meta?.success) toast.success(mutation.meta.success)
    },
  }),
})
